import {Frequency} from 'rrule';

import {SchedulerAction, SchedulerActionTypes} from './actions';
import {
  EndOption,
  Freq,
  WEEK_DAYS_SHORT,
} from '../../constants';
import {
  dateRoundUpHour,
  buildRRule,
  fromStrToRRule,
  convertStartDate,
} from '../../utils';

export const initialSchedulerState: ISchedulerState = {
  rrule: "",
  startDate: dateRoundUpHour(),
  tzId: Intl.DateTimeFormat().resolvedOptions().timeZone,
  freq: Freq.DAILY,
  interval: 1,
  byHour: [],
  weekdays: [],
  monthlyOn: 1,
  monthDay: 1,
  monthRelativeDay: 1,
  monthWeekDay: "MO",
  yearlyOn: 1,
  yearMonth: "1",
  yearMonthDay: 1,
  yearMonthRelativeDay: 1,
  yearMonthWeekDay: "MO",
  endOption: EndOption.NEVER,
  endAfter: 1,
  endOnDate: dateRoundUpHour(),
  error: '',
}

export interface ISchedulerState {
  rrule: string;
  startDate: Date;
  tzId: string | null;
  freq: Freq;
  // common
  interval: number; // used for Minutely, Hourly, Daily, Weekly, Monthly
  byHour: string[] | number[];
  // weekly
  weekdays: string[],
  // monthly
  monthlyOn: string | number; // monthly "on" or "on the"
  monthDay: string | number;
  monthRelativeDay: string | number;
  monthWeekDay: string;
  // yearly
  yearlyOn: string | number; // yearly "on" or "on the"
  yearMonth: string;
  yearMonthDay: string | number;
  yearMonthRelativeDay: string | number;
  yearMonthWeekDay: string;

  endOption: EndOption;
  endAfter: string | number;
  endOnDate: Date;
  error: string;
}

export function schedulerReducer(
  state: ISchedulerState = initialSchedulerState,
  action: SchedulerAction): ISchedulerState {

  switch (action.type) {
    case SchedulerActionTypes.SET_START_DATE:
      const newState = {
        ...state,
        startDate: dateRoundUpHour(),
      }

      return {
        ...newState,
        rrule: buildRRule(newState),
      }
    case SchedulerActionTypes.SET_SCHEDULER_KEY_VALUE:
      const {key, value} = action.payload;

      if (state.hasOwnProperty(key)) {
        let interval = state.interval;

        if (key === 'freq') {
          /*
           * If frequency changes, reset interval
           */
          interval = 1;
        }

        let startDate = state.startDate;
        if (key === 'byHour') {
          /*
           * In order to avoid a bug in RRule library, if byHour is changed, we set startDate to minimum `byHour`
           */
          if (value?.length > 0) {
            startDate = convertStartDate(state.startDate);
            startDate.setHours(Math.min(...value)); // value equals `byHour`
          }
        }

        const newState = {
          ...state,
          interval,
          startDate,
          [key]: value,
        };

        return {
          ...newState,
          rrule: buildRRule(newState),
        }
      }
      return state;

    case SchedulerActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      }

    case SchedulerActionTypes.SET_SCHEDULER_RRULE: {
      const rruleObj = fromStrToRRule(action.payload);

      const newState = {
        ...state,
        rrule: action.payload,
      }

      try {
        if (rruleObj) {
          const {options} = rruleObj;

          const freq = Frequency[options.freq];
          newState['startDate'] = convertStartDate(options.dtstart);
          newState['freq'] = freq as Freq;
          newState['tzId'] = (options.tzid || '').replace(';', ''); // trailing semicolon is added by rrule.js
          switch (freq) {
            case Freq.YEARLY:
              if (options.bymonth.length && options.bymonthday.length) {
                newState['yearlyOn'] = '1'; // On
                newState['yearMonth'] = '' + options.bymonth[0];
                newState['yearMonthDay'] = '' + options.bymonthday[0];
              } else if (options.bymonth.length && options.byweekday.length && options.bysetpos.length) {
                newState['yearlyOn'] = '2'; // On
                newState['yearMonth'] = '' + options.bymonth[0];
                newState['yearMonthRelativeDay'] = '' + options.bysetpos[0];
                newState['yearMonthWeekDay'] = options.byweekday
                  .map((weekdayIndex: number) => WEEK_DAYS_SHORT[weekdayIndex]).join(',');
              }
              break;
            case Freq.MONTHLY:
              newState['interval'] = options.interval;
              if (options.bysetpos?.length && options.byweekday?.length > 0) {
                newState['monthlyOn'] = '2'; // on the
                newState['monthRelativeDay'] = '' + options.bysetpos[0];
                newState['monthWeekDay'] = options.byweekday
                  .map((weekdayIndex: number) => WEEK_DAYS_SHORT[weekdayIndex]).join(',');
              } else if (options.bymonthday?.length) {
                newState['monthlyOn'] = '1'; // on day
                newState['monthDay'] = '' + options.bymonthday[0];
              } else if (options.bynmonthday?.length === 1 && options.bynmonthday[0] === -1) {
                newState['monthlyOn'] = '1'; // on day
                newState['monthDay'] = '-1'; // if -1, it means last day
              }
              break;
            case Freq.WEEKLY:
              newState['interval'] = options.interval;
              newState['weekdays'] = options
                .byweekday.map((id: number) => WEEK_DAYS_SHORT[id]); // 0 - Mo, 1 - Tu, etc.
              break;
            case Freq.DAILY:
              newState['interval'] = options.interval;
              break;
            case Freq.HOURLY:
            case Freq.MINUTELY:
              newState['interval'] = options.interval;

              if (options.byhour?.length > 0) {
                newState['byHour'] = options.byhour;
              }
              if (options.byweekday?.length > 0 && options.byweekday.length < 7) {
                /*
                 * At least one day set, but not all.
                 *
                 * If all days are set (length === 7), then we don't have to specify `by week day`, as default is
                 *   `all`.
                 */
                newState['weekdays'] = options
                  .byweekday.map((id: number) => WEEK_DAYS_SHORT[id]); // 0 - Mo, 1 - Tu, etc.
              }
              break;
          }

          if (options.count) {
            newState['endOption'] = EndOption.AFTER;
            newState['endAfter'] = options.count;
          } else if (options.until) {
            newState['endOption'] = EndOption.ON_DATE;
            newState['endOnDate'] = options.until;
          } else {
            newState['endOption'] = EndOption.NEVER;
          }
        }
      } catch (e) {
        console.log('ERROR: could not parse RRule:', action.payload);
      }

      return newState;
    }
    default:
      return state;
  }
}
